const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * The Razorpay rewards catalogue is a forward-only cursor, and reading only the
 * first response — which the shipped bundle did — hides roughly two thirds of
 * the store. Measured 2026-08-06: 276 sellable rewards over 3 pages.
 *
 * This is the one place that walks it, so the storefront listing and the
 * order-history image enrichment cannot disagree about how much of the
 * catalogue they saw.
 */

/** Provider hard cap. Values above this are clamped server-side, and asking the
 *  provider directly for more returns an empty body that reads as "no stock". */
export const MAX_PAGE_SIZE = 100;

/**
 * Razorpay refuses more than 4 of the same reward at the same denomination — and
 * it refuses them *after* the payment is captured, which produced paid orders
 * with no voucher. Every quantity control on the site is capped at this.
 *
 * It bounds one line, not the basket: a customer can hold any number of
 * different cards, or the same card at different denominations.
 */
export const GIFT_CARD_MAX_QUANTITY = 99999;

/** Safety stop. 3 pages is the real catalogue; this bounds a cursor that never
 *  drains rather than letting the tab spin. */
const MAX_PAGES = 12;

/**
 * The price a card is advertised at, in paise.
 *
 * `display_price` is the admin-marked-up starting price (face value + margin);
 * prefer it when present. Otherwise fall through to the face value: fixed cards
 * carry denominations in eligible_fixed_denomination, range (variable-amount)
 * cards carry [min, max] in eligible_range_denomination.
 *
 * Shared so the card and the price filter cannot disagree about what a reward
 * costs — a card reading "Starts @ ₹252.50" must land in the ₹250–₹500 bucket.
 */
export function getRewardStartingPaise(reward) {
  return (
    reward?.display_price
    ?? reward?.eligible_fixed_denomination?.[0]
    ?? reward?.eligible_range_denomination?.[0]
    ?? reward?.amount
    ?? 0
  );
}

/**
 * Walks every page and returns the full list.
 *
 * @param {object}   opts
 * @param {function} opts.onPage   called with (itemsSoFar, meta) after each page,
 *                                 so a caller can paint progressively.
 * @param {function} opts.isCancelled  checked between pages; lets a caller abandon
 *                                 the walk when its effect is torn down.
 * @returns {{ items, providerCount, pages, complete }}
 *   `providerCount` is the provider's own total for the *unfiltered* catalogue
 *   (277). It is not the number of items you received — the server hides
 *   unsellable rewards — so never use it for "showing N of …".
 */
export async function fetchAllRewards({ onPage, isCancelled } = {}) {
  const items = [];
  let pageId = null;
  let providerCount = null;
  let pages = 0;
  let complete = false;

  for (let i = 0; i < MAX_PAGES; i++) {
    if (isCancelled?.()) break;

    const qs = new URLSearchParams({ count: String(MAX_PAGE_SIZE) });
    if (pageId) qs.set('page_id', pageId);

    const response = await fetch(`${API}/giftcards/razorpay/rewards?${qs}`, {
      credentials: 'include',
    });
    if (!response.ok) break;

    const data = await response.json();
    const pageItems = data.items || [];
    pages += 1;
    if (providerCount == null) providerCount = data.count ?? null;

    // The backend already drops non-purchasable rewards, but a cached response
    // may predate that, and the filter costs nothing.
    items.push(...pageItems.filter((item) => item.type === 'gift_card'));
    onPage?.(items, { pages, providerCount });

    pageId = data.next_page_id;

    // The cursor overruns by one: the response that drains the catalogue still
    // hands back a next_page_id, and following it costs a round-trip to be told
    // there is nothing there. An empty page is the real end.
    if (!pageId || !pageItems.length) {
      complete = true;
      break;
    }
  }

  return { items, providerCount, pages, complete };
}
